import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import {
  DIRECT_SOURCE,
  FUNNEL_STAGES,
  FUNNEL_STAGE_LABEL,
  IMOVEL_LABEL,
  LEAD_FILTERS,
  LEAD_FILTER_LABEL,
  MOTIVOS,
  MOTIVO_LABEL,
  PERIODS,
  PERIOD_LABEL,
  REGIAO_LABEL,
  type FunnelStageKey,
  type LeadFilterKey,
  type MotivoKey,
  type PeriodKey,
} from '@sc/shared/domain';
import { ExampleBadge, StageBadge, TierBadge } from '../components/badges';
import { api, qs } from '../lib/api';
import { formatBRL, formatInt, formatUsdMicro, relativeTime } from '../lib/format';
import { LeadDetailPanel } from './LeadDetailPanel';
import type { LeadRow, Page, Stats } from './types';

const BAR_COLORS = ['#c9d6ec', '#9db6e0', '#5f8fd0', '#f89b26', '#17a35a'];

function useDebounced<T>(value: T, ms = 300) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

/** Lead aberto pelo link do e-mail de aviso (/admin?lead=123). */
function leadDaUrl(): number | null {
  const n = Number(new URLSearchParams(window.location.search).get('lead'));
  return Number.isSafeInteger(n) && n > 0 ? n : null;
}

const label = (map: Record<string, string>, k: string | null) => (k ? (map[k] ?? k) : null);

export function LeadsPage() {
  const [period, setPeriod] = useState<PeriodKey>('30d');
  const [source, setSource] = useState('');
  const [filter, setFilter] = useState<LeadFilterKey>('todos');
  const [search, setSearch] = useState('');
  const [etapa, setEtapa] = useState<FunnelStageKey | ''>('');
  const [motivo, setMotivo] = useState<MotivoKey | ''>('');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<number | null>(leadDaUrl);
  const q = useDebounced(search.trim());

  useEffect(() => setPage(1), [period, source, filter, q, etapa, motivo]);

  const params = {
    period,
    source: source || undefined,
    filter,
    q: q || undefined,
    etapa: etapa || undefined,
    motivo: motivo || undefined,
    page,
  };
  const stats = useQuery({
    queryKey: ['admin', 'stats', period, source],
    queryFn: () => api.get<Stats>(`/admin/stats${qs({ period, source })}`),
  });
  const list = useQuery({
    queryKey: ['admin', 'leads', params],
    queryFn: () => api.get<Page<LeadRow>>(`/admin/leads${qs(params)}`),
    placeholderData: keepPreviousData,
  });

  const s = stats.data;
  const first = s?.funnel[0]?.count || 1;
  const pages = list.data ? Math.max(1, Math.ceil(list.data.total / list.data.pageSize)) : 1;

  return (
    <div className="split">
      <main className="main" id="conteudo">
        <header className="page-header">
          <h1>Leads do chat</h1>
          <div className="toolbar">
            <label className="sr-only" htmlFor="search">
              Buscar por nome, telefone, número do lead ou protocolo
            </label>
            <input
              id="search"
              className="pill"
              type="search"
              placeholder="Buscar nome, telefone ou protocolo"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <label className="sr-only" htmlFor="period">
              Período
            </label>
            <select id="period" className="pill" value={period} onChange={(e) => setPeriod(e.target.value as PeriodKey)}>
              {PERIODS.map((p) => (
                <option key={p} value={p}>
                  {PERIOD_LABEL[p]}
                </option>
              ))}
            </select>
            <label className="sr-only" htmlFor="source">
              Origem
            </label>
            <select id="source" className="pill" value={source} onChange={(e) => setSource(e.target.value)}>
              <option value="">Todas as origens</option>
              {s?.sources.map((src) => (
                <option key={src} value={src}>
                  {src === DIRECT_SOURCE ? 'Direto (sem campanha)' : src}
                </option>
              ))}
            </select>
            <label className="sr-only" htmlFor="etapa">
              Etapa do funil
            </label>
            <select id="etapa" className="pill" value={etapa} onChange={(e) => setEtapa(e.target.value as FunnelStageKey | '')}>
              <option value="">Todas as etapas</option>
              {FUNNEL_STAGES.map((st) => (
                <option key={st} value={st}>
                  {FUNNEL_STAGE_LABEL[st]}
                </option>
              ))}
            </select>
            <label className="sr-only" htmlFor="motivo">
              Motivo de fora do perfil
            </label>
            <select id="motivo" className="pill" value={motivo} onChange={(e) => setMotivo(e.target.value as MotivoKey | '')}>
              <option value="">Todos os motivos</option>
              {MOTIVOS.map((m) => (
                <option key={m} value={m}>
                  {MOTIVO_LABEL[m]}
                </option>
              ))}
            </select>
            <a className="btn btn-secondary" href={`/api/admin/leads.csv${qs({ period, source, filter, q, etapa, motivo })}`} download>
              Exportar CSV
            </a>
          </div>
        </header>

        <section className="kpis" aria-label="Indicadores do período">
          <div className="kpi">
            <span className="kpi-label">Abriram o chat</span>
            <span className="kpi-value">{s ? formatInt(s.kpis.leads) : '—'}</span>
            {s && s.foraDoPerfil > 0 && <span className="kpi-note">{formatInt(s.foraDoPerfil)} fora do perfil</span>}
          </div>
          <div className="kpi">
            <span className="kpi-label">Enviaram o contato</span>
            <span className="kpi-value">{s ? formatInt(s.kpis.whatsapp) : '—'}</span>
            {s && <span className="kpi-note">{formatInt(s.kpis.hot)} quentes</span>}
          </div>
          <div className="kpi">
            <span className="kpi-label">Fecharam</span>
            <span className="kpi-value">{s ? formatInt(s.kpis.converted) : '—'}</span>
            {s && s.kpis.closedValueCents > 0 && <span className="kpi-note">{formatBRL(s.kpis.closedValueCents)} em projetos</span>}
          </div>
          <div className="kpi">
            <span className="kpi-label">Custo de IA</span>
            <span className="kpi-value">{s ? formatUsdMicro(s.kpis.aiCostMicroUsd) : '—'}</span>
            {s && (
              <span className="kpi-note">
                {formatInt(s.kpis.aiCalls)} chamadas em {formatInt(s.kpis.conversations)} conversas
              </span>
            )}
          </div>
          {s && s.kpis.pendingNotices > 0 && (
            <div className="kpi">
              <span className="kpi-label">Avisos por e-mail pendentes</span>
              <span className="kpi-value">{formatInt(s.kpis.pendingNotices)}</span>
              <span className="kpi-note">Confira a chave do Resend</span>
            </div>
          )}
        </section>

        <section className="card funnel" aria-labelledby="funil-title">
          <div className="card-head" style={{ padding: 0 }}>
            <h2 id="funil-title">Do chat ao fechamento</h2>
            {s?.hasExampleData && <ExampleBadge />}
          </div>
          <ol className="funnel-steps">
            {s?.funnel.map((f, i) => (
              <li key={f.key} className="funnel-step">
                <div
                  className="funnel-bar"
                  style={{ width: `${Math.max(0, (f.count / first) * 100)}%`, background: BAR_COLORS[i] }}
                  aria-hidden="true"
                />
                <span className="funnel-count">{formatInt(f.count)}</span>
                <span className="funnel-label">{f.label}</span>
                {i > 0 && <span className="funnel-rate">{Math.round((f.count / first) * 100)}% de quem abriu</span>}
              </li>
            ))}
          </ol>
        </section>

        <section className="card" aria-labelledby="leads-title">
          <div className="card-head">
            <h2 id="leads-title">Leads</h2>
            <div className="chips" role="group" aria-label="Filtrar leads">
              {LEAD_FILTERS.map((f) => (
                <button key={f} type="button" className="chip" aria-pressed={filter === f} onClick={() => setFilter(f)}>
                  {LEAD_FILTER_LABEL[f]}
                </button>
              ))}
            </div>
          </div>
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th scope="col">Lead</th>
                  <th scope="col">Prioridade</th>
                  <th scope="col">Imóvel</th>
                  <th scope="col">Conta de luz</th>
                  <th scope="col">Região</th>
                  <th scope="col">Etapa</th>
                  <th scope="col">Origem</th>
                  <th scope="col">Atividade</th>
                </tr>
              </thead>
              <tbody>
                {list.data?.items.map((l) => (
                  <tr key={l.id} aria-selected={selected === l.id} onClick={() => setSelected(l.id)}>
                    <td>
                      <button type="button" className="row-link" onClick={() => setSelected(l.id)} aria-label={`Abrir lead ${l.id}`}>
                        #{l.id}
                      </button>
                      {l.name && <span className="small"> {l.name}</span>}
                      <br />
                      <span className="mono small">{l.phoneMasked}</span>
                    </td>
                    <td>
                      <TierBadge tier={l.tier} score={l.score} senior={l.senior} manutencao={l.objective === 'manutencao'} />
                    </td>
                    <td className="small">{label(IMOVEL_LABEL, l.propertyType) ?? <span className="muted">—</span>}</td>
                    <td className="mono">
                      {l.monthlyBillCents !== null ? `${l.billApproximate ? '~' : ''}${formatBRL(l.monthlyBillCents)}` : <span className="muted">—</span>}
                    </td>
                    <td className="small">{label(REGIAO_LABEL, l.region) ?? <span className="muted">—</span>}</td>
                    <td>
                      <StageBadge stage={l.displayStage} />
                      {l.motivo && <div className="small">{MOTIVO_LABEL[l.motivo]}</div>}
                    </td>
                    <td className="small">{l.utmSource ?? 'Direto'}</td>
                    <td className="small">{relativeTime(l.lastActivityAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {list.data && list.data.items.length === 0 && <p className="empty">Nenhum lead com esses filtros.</p>}
            {list.isError && <p className="alert alert-error">Não foi possível carregar os leads.</p>}
          </div>
          {list.data && list.data.total > 0 && (
            <div className="pager">
              <span>
                {formatInt(list.data.total)} leads · página {page} de {pages}
              </span>
              <span className="toolbar">
                <button type="button" className="btn btn-secondary" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                  Anterior
                </button>
                <button type="button" className="btn btn-secondary" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>
                  Próxima
                </button>
              </span>
            </div>
          )}
        </section>
      </main>
      {selected !== null && <LeadDetailPanel leadId={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}
